import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Chip, Collapse, MenuItem, Skeleton, Stack, TextField, Tooltip as MuiTooltip, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import DownloadPincodesButton from './components/DownloadPincodesButton'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { useParams } from 'react-router-dom'
import { intelApi } from './api'
import { intelColors, formatNumber, formatPct } from './components/intelTheme'
import { SERV_MODE_BY_KEY, resolveServiceTypes } from './servModes'

const DIRECTIONS = [
  { val: 'ALL', label: 'Both Forward & Reverse' },
  { val: 'FORWARD', label: 'Forward Only' },
  { val: 'REVERSE', label: 'Reverse Only' },
]

const CATEGORY_LABEL = { FORWARD: 'Forward', REVERSE: 'Reverse', HIGH_VALUE: 'High Value', SDD_NDD: 'SDD / NDD', HYPERLOCAL: 'Hyperlocal' }
const categoryColor = (c) => (c === 'REVERSE' ? 'orange' : c === 'HIGH_VALUE' ? 'purple' : c === 'SDD_NDD' ? 'green' : c === 'HYPERLOCAL' ? 'grey' : 'blue')

const payModeBadge = (m) => {
  const map = { BOTH: { color: 'green', label: 'Both' }, COD: { color: 'orange', label: 'COD' }, PREPAID: { color: 'blue', label: 'Prepaid' } }
  const cfg = map[m] || { color: 'grey', label: '—' }
  return <Badge color={cfg.color}>{cfg.label}</Badge>
}

const stateBadge = (s) => {
  const map = {
    ACTIVE: { color: 'green', label: '✓ Active' },
    PARTIAL: { color: 'orange', label: 'Partial' },
    OVERLAY_ONLY: { color: 'grey', label: 'Overlay only' },
    PENDING_SYNC: { color: 'grey', label: 'Pending sync' },
  }
  const cfg = map[s] || { color: 'grey', label: s || '—' }
  return <Badge color={cfg.color}>{cfg.label}</Badge>
}

// ── Section colors — one per data view for instant visual distinction ────────
const SECTION_THEME = {
  stype:   { accent: intelColors.primary, bg: intelColors.badgeBlueBg, label: 'Service Types', icon: '◈' },
  courier: { accent: intelColors.green, bg: intelColors.badgeGreenBg, label: 'Couriers', icon: '◉' },
  code:    { accent: intelColors.purple, bg: intelColors.badgePurpleBg, label: 'Courier Codes', icon: '◎' },
}

// ── Reusable collapsible section header ──────────────────────────────────────
function SectionHeader({ sectionKey, expanded, onToggle, count, subtitle }) {
  const theme = SECTION_THEME[sectionKey]
  const isOpen = expanded
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.5,
        mb: isOpen ? 0 : 1.5,
        borderRadius: isOpen ? '8px 8px 0 0' : '8px',
        border: `1px solid ${isOpen ? theme.accent : intelColors.border}`,
        borderBottom: isOpen ? `2px solid ${theme.accent}` : `1px solid ${intelColors.border}`,
        background: isOpen ? theme.bg : '#fff',
        cursor: 'pointer',
        transition: 'all 0.15s',
        userSelect: 'none',
        '&:hover': { borderColor: theme.accent, background: theme.bg },
      }}
    >
      {/* Chevron — left side, prominent */}
      <Box sx={{ color: theme.accent, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {isOpen
          ? <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
          : <KeyboardArrowRightIcon sx={{ fontSize: 22 }} />
        }
      </Box>

      {/* Section label */}
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text, letterSpacing: '0.2px' }}>
        {theme.label}
      </Typography>

      {/* Count chip */}
      <Chip size='small' label={count}
        sx={{
          fontSize: 11, fontWeight: 700, height: 22,
          bgcolor: isOpen ? theme.accent : intelColors.border,
          color: isOpen ? '#fff' : intelColors.subtext,
        }}
      />

      {/* Subtitle */}
      {subtitle && (
        <Typography sx={{ fontSize: 11, color: intelColors.muted, ml: 'auto' }}>
          {subtitle}
        </Typography>
      )}

      {/* Expand hint when collapsed */}
      {!isOpen && (
        <Typography sx={{ fontSize: 10, color: intelColors.muted, ml: 'auto', fontStyle: 'italic' }}>
          Click to expand
        </Typography>
      )}
    </Box>
  )
}

// ── Column definitions (extracted for reuse) ────────────────────────────────
const buildStypeColumns = (filterPincode) => [
  { key: 'name', header: 'Service Type', info: 'service_type_master — every ST is listed, even those no active courier maps.', render: (r) => <strong>{r.name || r.code}</strong> },
  { key: 'cat', header: 'Category', info: 'service_type_master.category (Forward / Reverse / High Value / SDD-NDD / Hyperlocal).', render: (r) => <Badge color={categoryColor(r.category)}>{CATEGORY_LABEL[r.category] || r.category}</Badge> },
  { key: 'dir', header: 'Direction', info: 'service_type_master.direction — FORWARD or RVP (reverse).', render: (r) => <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.direction === 'RVP' ? 'Reverse' : r.direction === 'FORWARD' ? 'Forward' : r.direction || '—'}</Badge> },
  { key: 'srv', header: 'Serviceable?', info: 'ACTIVE if ≥1 active courier maps this ST as primary; OVERLAY_ONLY if none.', render: (r) => stateBadge(r.serviceable) },
  { key: 'codes', header: '# Codes', info: 'Count of active courier codes whose service_type_primary = this ST.', render: (r) => r.codeCount ?? '—' },
  { key: 'pay', header: 'Payment Mode', info: 'Payment modes the ST\'s active couriers collectively support.', render: (r) => payModeBadge(r.paymentMode) },
  { key: 'pickup', header: 'Pickup Pincodes', info: 'Distinct union of pickupPincodesPrepaid across active codes.', render: (r) => formatNumber(r.pickupPincodes) },
  { key: 'deliv', header: 'Delivery Pincodes', info: 'Distinct union of dropPincodesPrepaid ∪ dropPincodesCOD (de-duplicated).', render: (r) => formatNumber(r.deliveryPincodes) },
  { key: 'rem', header: 'Description', info: 'service_type_master.description.', render: (r) => <span style={{ color: intelColors.subtext, fontSize: 11 }}>{r.remarks}</span> },
  {
    key: 'dl', header: 'Pincodes', info: 'Download the actual pincode list (not just the count) for the account codes behind this row.',
    render: (r) => (
      <DownloadPincodesButton
        accountCodes={r.accountCodesOffered}
        filterPincode={filterPincode}
        filenameBase={`service_type_${r.code || r.name}`}
        options={[
          { key: 'pickup', label: 'Pickup Pincodes', count: r.pickupPincodes, pincodeTypes: ['PICKUP_PREPAID'] },
          { key: 'delivery', label: 'Delivery Pincodes', count: r.deliveryPincodes, pincodeTypes: ['DROP_PREPAID', 'DROP_COD'] },
        ]}
      />
    ),
  },
]

const buildCourierColumns = (filterPincode) => [
  { key: 'parent', header: 'Parent Courier', info: 'courier_partners.parent — active codes grouped by parent.', render: (r) => <strong>{r.parent}</strong> },
  { key: 'codes', header: 'Account Codes', info: 'Distinct set of cp_account_code across this parent\'s active codes.', render: (r) => (r.accountCodesOffered || []).join(', ') || '—' },
  { key: 'fwd', header: 'Forward Codes', info: 'Count of this parent\'s active codes with type = FORWARD.', render: (r) => r.fwdCodeCount ?? '—' },
  { key: 'rev', header: 'Reverse Codes', info: 'Count of this parent\'s active codes with type = RVP (reverse).', render: (r) => r.revCodeCount ?? '—' },
  { key: 'pins', header: 'Total Pincodes (Σ)', info: 'SUM of dropPrepaidPincodeCount + dropCodPincodeCount. NOT de-duplicated.', render: (r) => formatNumber(r.totalPincodesSum) },
  { key: 'sdd', header: 'SDD-eligible', info: 'Count of the parent\'s codes flagged sdd_eligible = true.', render: (r) => r.sddEligibleCount ?? '—' },
  { key: 'ndd', header: 'NDD-eligible', info: 'Count of the parent\'s codes flagged ndd_eligible = true.', render: (r) => r.nddEligibleCount ?? '—' },
  { key: 'stypes', header: 'Service Types Offered', info: 'Distinct set of service_type_primary across this parent\'s codes.', render: (r) => (r.serviceTypesOffered || []).join(', ') },
  {
    key: 'dl', header: 'Pincodes', info: 'Download the actual delivery pincode list (merged across every account code under this parent, de-duplicated).',
    render: (r) => (
      <DownloadPincodesButton
        accountCodes={r.accountCodesOffered}
        filterPincode={filterPincode}
        filenameBase={`courier_${r.parent}`}
        options={[{ key: 'delivery', label: 'Delivery Pincodes', count: r.totalPincodesSum, pincodeTypes: ['DROP_PREPAID', 'DROP_COD'] }]}
      />
    ),
  },
]

const buildCodeColumns = (filterPincode) => [
  { key: 'code', header: 'Courier Code', info: 'courier_partners.cp_account_code (one row per active code).', render: (r) => <code style={{ background: intelColors.headRowBg, padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>{r.cpAccountCode}</code> },
  { key: 'parent', header: 'Parent Courier', info: 'courier_partners.parent.', render: (r) => r.parent },
  { key: 'st', header: 'Service Type', info: 'courier_partners.service_type_primary.', render: (r) => r.serviceTypePrimary },
  { key: 'dir', header: 'Direction', info: 'courier_partners.type — FORWARD or RVP (reverse).', render: (r) => <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.direction === 'RVP' ? 'Reverse' : r.direction === 'FORWARD' ? 'Forward' : r.direction || '—'}</Badge> },
  { key: 'pins', header: 'Total Pincodes', info: 'dropPrepaidPincodeCount + dropCodPincodeCount.', render: (r) => formatNumber(r.totalPincodes) },
  { key: 'metro', header: 'Metro', info: 'courier_partners.metro_serviceable flag.', render: (r) => (r.metroServiceable ? '✓' : '—') },
  { key: 'sdd', header: 'SDD', info: 'courier_partners.sdd_eligible flag.', render: (r) => (r.sddEligible ? '✓' : '—') },
  { key: 'ndd', header: 'NDD', info: 'courier_partners.ndd_eligible flag.', render: (r) => (r.nddEligible ? '✓' : '—') },
  { key: 'active', header: 'Active', info: 'courier_partners.isActive.', render: (r) => (r.isActive ? <Badge color='green'>Yes</Badge> : <Badge color='grey'>No</Badge>) },
  {
    key: 'dl', header: 'Pincodes', info: 'Download the actual delivery pincode list for this one account code.',
    render: (r) => (
      <DownloadPincodesButton
        accountCodes={[r.cpAccountCode]}
        filterPincode={filterPincode}
        filenameBase={`code_${r.cpAccountCode}`}
        options={[{ key: 'delivery', label: 'Delivery Pincodes', count: r.totalPincodes, pincodeTypes: ['DROP_PREPAID', 'DROP_COD'] }]}
      />
    ),
  },
]

// ── Client-side CSV export ───────────────────────────────────────────────────
// Exports the FULL in-state rows (never IntelTable's paginated slice) so "no API calls,
// handle pagination correctly" holds: the whole dataset is already in the component's state.
const csvEsc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
const exportCsv = (filename, cols, rows) => {
  const body = [cols.map((c) => csvEsc(c.label)).join(',')]
  rows.forEach((r) => body.push(cols.map((c) => csvEsc(c.get(r))).join(',')))
  const blob = new Blob([body.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
const dirText = (d) => (d === 'RVP' ? 'Reverse' : d === 'FORWARD' ? 'Forward' : d || '')
const STYPE_CSV = [
  { label: 'Service Type', get: (r) => r.name || r.code },
  { label: 'Category', get: (r) => CATEGORY_LABEL[r.category] || r.category },
  { label: 'Direction', get: (r) => dirText(r.direction) },
  { label: 'Serviceable', get: (r) => r.serviceable },
  { label: '# Codes', get: (r) => r.codeCount },
  { label: 'Payment Mode', get: (r) => r.paymentMode },
  { label: 'Pickup Pincodes', get: (r) => r.pickupPincodes },
  { label: 'Delivery Pincodes', get: (r) => r.deliveryPincodes },
  { label: 'Description', get: (r) => r.remarks },
]
const COURIER_CSV = [
  { label: 'Parent Courier', get: (r) => r.parent },
  { label: 'Account Codes', get: (r) => (r.accountCodesOffered || []).join('; ') },
  { label: 'Forward Codes', get: (r) => r.fwdCodeCount },
  { label: 'Reverse Codes', get: (r) => r.revCodeCount },
  { label: 'Total Pincodes (sum)', get: (r) => r.totalPincodesSum },
  { label: 'SDD-eligible', get: (r) => r.sddEligibleCount },
  { label: 'NDD-eligible', get: (r) => r.nddEligibleCount },
  { label: 'Service Types Offered', get: (r) => (r.serviceTypesOffered || []).join('; ') },
]
const CODE_CSV = [
  { label: 'Courier Code', get: (r) => r.cpAccountCode },
  { label: 'Parent Courier', get: (r) => r.parent },
  { label: 'Service Type', get: (r) => r.serviceTypePrimary },
  { label: 'Direction', get: (r) => dirText(r.direction) },
  { label: 'Total Pincodes', get: (r) => r.totalPincodes },
  { label: 'Metro', get: (r) => (r.metroServiceable ? 'Yes' : 'No') },
  { label: 'SDD', get: (r) => (r.sddEligible ? 'Yes' : 'No') },
  { label: 'NDD', get: (r) => (r.nddEligible ? 'Yes' : 'No') },
  { label: 'Active', get: (r) => (r.isActive ? 'Yes' : 'No') },
]
const ExportCsvButton = ({ onClick, disabled }) => (
  <Button
    size='small' variant='outlined' disabled={disabled} onClick={onClick}
    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, py: 0.25, borderColor: '#c5d8f5', color: '#1565c0', '&:hover': { borderColor: '#1565c0', bgcolor: '#f5f9ff' } }}
  >
    Export CSV
  </Button>
)

export default function CoverageDelivery() {
  const { mode: modeKey } = useParams()
  const activeMode = modeKey ? SERV_MODE_BY_KEY[modeKey] : null

  const [direction, setDirection] = useState(activeMode?.direction || 'ALL')
  const [parentCp, setParentCp] = useState('')
  const [serviceType, setServiceType] = useState(activeMode ? activeMode.serviceTypes.join(',') : '')
  const [accountCode, setAccountCode] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [pincode, setPincode] = useState('')
  const [lookupRes, setLookupRes] = useState(null)
  const [lookupLoad, setLookupLoad] = useState(false)
  const [parentOptions, setParentOptions] = useState([])
  const [serviceTypeOptions, setServiceTypeOptions] = useState([])
  const [accountCodeOptions, setAccountCodeOptions] = useState([])
  // Accordion expand state — Service Types open by default
  const [expanded, setExpanded] = useState({ stype: true, courier: false, code: false })

  const toggleSection = (key) => () => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

  useEffect(() => {
    let cancel = false
    intelApi
      .formOptions()
      .then((res) => {
        if (cancel) return
        const opts = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setParentOptions(opts.parentCps || [])
        setServiceTypeOptions(opts.serviceTypes || [])
      })
      .catch(() => {})
    return () => { cancel = true }
  }, [])

  useEffect(() => {
    if (!parentCp) { setAccountCodeOptions([]); return }
    let cancel = false
    intelApi
      .accountCodes({ parent_cp: parentCp })
      .then((res) => {
        if (cancel) return
        const body = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setAccountCodeOptions((body.accountCodes || []).map((c) => c.accountCode || c).filter(Boolean))
      })
      .catch(() => !cancel && setAccountCodeOptions([]))
    return () => { cancel = true }
  }, [parentCp])

  useEffect(() => {
    if (!activeMode) return
    setDirection(activeMode.direction || 'ALL')
    setParentCp('')
    setAccountCode('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKey])

  // Resolve the mode's service-type codes to the backend's exact strings (from
  // form-options) so casing can never drift; keep the scoped filter in sync.
  const modeCodes = useMemo(
    () => (activeMode ? resolveServiceTypes(activeMode.serviceTypes, serviceTypeOptions) : []),
    [activeMode, serviceTypeOptions]
  )
  useEffect(() => {
    if (activeMode) setServiceType(modeCodes.join(','))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeCodes])

  // The backend computes exactly ONE breakdown table per call — whichever `viewMode` was
  // requested (KPIs/tierStrip/topCouriers come back on every call regardless). STYPE drives
  // the KPI cards + the Service Types accordion, which is open by default, so fetch it eagerly.
  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .coverageDelivery({ viewMode: 'STYPE', direction, parentCp, serviceType, accountCode })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => { cancel = true }
  }, [direction, parentCp, serviceType, accountCode])

  // Couriers / Courier Codes accordions each need their OWN call with viewMode COURIER / CODE
  // respectively — fetched lazily (only once expanded) and re-fetched only when the filters
  // actually change since the last fetch, so collapsed accordions never cost a network call.
  //
  // Two separate keys matter here: `courierReqKeyRef` (what we last FIRED a request for, used
  // only to dedupe/avoid re-fetching) vs `courierData`'s own `courierDataKey` state (what the
  // data CURRENTLY HELD actually corresponds to). Switching a side-menu mode changes `direction`
  // and `serviceType` via two separate effects, so there's a render in between where filterKey
  // is a mismatched combination of old+new values — without this split, that intermediate
  // (wrong) fetch's request-key would make the OLD data look "fresh" the instant it's fired,
  // flashing a stale/incorrect count (often 0) before the corrected fetch lands.
  const filterKey = `${direction}|${parentCp}|${serviceType}|${accountCode}`

  const [courierData, setCourierData] = useState(null)
  const [courierDataKey, setCourierDataKey] = useState(null)
  const [courierLoading, setCourierLoading] = useState(false)
  const courierReqKeyRef = useRef(null)
  useEffect(() => {
    if (!expanded.courier || courierReqKeyRef.current === filterKey) return
    let cancel = false
    courierReqKeyRef.current = filterKey
    setCourierLoading(true)
    intelApi
      .coverageDelivery({ viewMode: 'COURIER', direction, parentCp, serviceType, accountCode })
      .then((res) => {
        if (cancel) return
        setCourierData(res?.data?.responseObj || res?.data?.data || res?.data)
        setCourierDataKey(filterKey)
      })
      .catch(() => { if (!cancel) courierReqKeyRef.current = null }) // allow a retry on next expand/filter change
      .finally(() => !cancel && setCourierLoading(false))
    return () => { cancel = true }
  }, [expanded.courier, filterKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const courierFresh = expanded.courier && courierDataKey === filterKey

  const [codeData, setCodeData] = useState(null)
  const [codeDataKey, setCodeDataKey] = useState(null)
  const [codeLoading, setCodeLoading] = useState(false)
  const codeReqKeyRef = useRef(null)
  useEffect(() => {
    if (!expanded.code || codeReqKeyRef.current === filterKey) return
    let cancel = false
    codeReqKeyRef.current = filterKey
    setCodeLoading(true)
    intelApi
      .coverageDelivery({ viewMode: 'CODE', direction, parentCp, serviceType, accountCode })
      .then((res) => {
        if (cancel) return
        setCodeData(res?.data?.responseObj || res?.data?.data || res?.data)
        setCodeDataKey(filterKey)
      })
      .catch(() => { if (!cancel) codeReqKeyRef.current = null })
      .finally(() => !cancel && setCodeLoading(false))
    return () => { cancel = true }
  }, [expanded.code, filterKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const codeFresh = expanded.code && codeDataKey === filterKey

  const runLookup = () => {
    if (!/^\d{6}$/.test(pincode)) { setLookupRes({ error: 'Enter a valid 6-digit pincode' }); return }
    setLookupLoad(true)
    intelApi
      .coveragePincodeLookup({ pincode })
      .then((res) => setLookupRes(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => setLookupRes({ error: e?.response?.data?.exception || e.message }))
      .finally(() => setLookupLoad(false))
  }

  // Scope the breakdown tables to the selected service type(s) — whether chosen via the
  // by-delivery-mode route OR the Service Type filter — so the filtered broader view shows
  // the same rows as the by-mode page. (KPIs are already scoped by serviceType server-side.)
  const selectedStypeSet = serviceType
    ? new Set(serviceType.split(',').map((s) => s.trim()).filter(Boolean))
    : null
  const kpis = data?.kpis || {}
  const tiers = data?.tierStrip || []
  const topCouriers = data?.topCouriersByCoverage || []
  const stypeRows = (data?.breakdownStype || []).filter((r) => !selectedStypeSet || selectedStypeSet.has(r.code))
  // Only trust the rows once courierFresh/codeFresh confirm the data matches the CURRENT
  // filters — otherwise (collapsed, loading, or mid-transition between side-menu modes)
  // render nothing so the table/header fall back to their loading/placeholder state.
  const courierRows = courierFresh ? (courierData?.breakdownCourier || []) : []
  const codeRows = codeFresh
    ? (codeData?.breakdownCode || []).filter((r) => !selectedStypeSet || selectedStypeSet.has(r.serviceTypePrimary))
    : []

  // Narrow "Download Pincodes" to the searched pincode only once it's a complete 6-digit
  // value — a partial in-progress typed value should never be treated as an active filter.
  const filterPincode = /^\d{6}$/.test(pincode) ? pincode : undefined

  const stypeColumns = useMemo(() => buildStypeColumns(filterPincode), [filterPincode])
  const courierColumns = useMemo(() => buildCourierColumns(filterPincode), [filterPincode])
  const codeColumns = useMemo(() => buildCodeColumns(filterPincode), [filterPincode])

  // Counts for section headers
  const stypeActiveCount = stypeRows.filter((r) => r.serviceable === 'ACTIVE').length
  const courierFwdCount = courierRows.reduce((s, r) => s + (r.fwdCodeCount || 0), 0)
  const courierRevCount = courierRows.reduce((s, r) => s + (r.revCodeCount || 0), 0)
  const codeFwdCount = codeRows.filter((r) => r.direction === 'FORWARD').length
  const codeRevCount = codeRows.filter((r) => r.direction === 'RVP').length

  const directionLabel = direction === 'FORWARD' ? 'forward shipments only' : direction === 'REVERSE' ? 'reverse shipments only' : ''

  return (
    <IntelLayout title={activeMode ? `${activeMode.label} — Serviceability` : 'Delivery Coverage'}>
      {/* ── Lookup / filter bar ── */}
      <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', padding: '14px 18px', mb: 2, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: intelColors.subtext, textTransform: 'uppercase', mb: '4px' }}>Search Pincode</Typography>
          <TextField size='small' placeholder='6-digit pincode' value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            sx={{ width: 220, '& input': { fontSize: 12, padding: '7px 10px' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: intelColors.subtext, textTransform: 'uppercase', mb: '4px' }}>Parent Courier</Typography>
          <TextField select size='small' value={parentCp}
            onChange={(e) => { setParentCp(e.target.value); setAccountCode('') }}
            SelectProps={{ displayEmpty: true }}
            sx={{ width: 180, '& .MuiSelect-select': { fontSize: 12, padding: '7px 10px' } }}>
            <MenuItem value='' sx={{ fontSize: 12 }}>All Parents</MenuItem>
            {parentOptions.map((p) => <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>{p}</MenuItem>)}
          </TextField>
        </Box>
        {!activeMode && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: intelColors.subtext, textTransform: 'uppercase', mb: '4px' }}>Service Type</Typography>
            <TextField select size='small' value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{ width: 200, '& .MuiSelect-select': { fontSize: 12, padding: '7px 10px' } }}>
              <MenuItem value='' sx={{ fontSize: 12 }}>All Service Types</MenuItem>
              {serviceTypeOptions.map((s) => <MenuItem key={s.value} value={s.value} sx={{ fontSize: 12 }}>{s.label || s.value}</MenuItem>)}
            </TextField>
          </Box>
        )}
        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: intelColors.subtext, textTransform: 'uppercase', mb: '4px' }}>Courier Code</Typography>
          <TextField select size='small' value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            disabled={!parentCp} SelectProps={{ displayEmpty: true }}
            helperText={!parentCp ? 'Select a parent first' : ''}
            sx={{ width: 220, '& .MuiSelect-select': { fontSize: 12, padding: '7px 10px' }, '& .MuiFormHelperText-root': { fontSize: 10, m: 0, position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap' } }}>
            <MenuItem value='' sx={{ fontSize: 12 }}>All Codes</MenuItem>
            {accountCodeOptions.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </TextField>
        </Box>
        <Box onClick={runLookup}
          sx={{ background: intelColors.primary, color: '#fff', borderRadius: '5px', padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', '&:hover': { background: intelColors.primaryDark } }}>
          Lookup PIN
        </Box>
        <Box onClick={() => { setParentCp(''); setAccountCode(''); setPincode(''); setLookupRes(null); setServiceType(activeMode ? modeCodes.join(',') : ''); setDirection(activeMode ? activeMode.direction || 'ALL' : 'ALL') }}
          sx={{ background: '#fff', color: intelColors.red, border: `1px solid ${intelColors.red}`, borderRadius: '5px', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Reset
        </Box>
      </Box>

      {lookupRes && (
        <Box sx={{ background: intelColors.badgeBlueBg, border: '1px solid #90caf9', borderRadius: '6px', p: '10px 14px', mb: 2, fontSize: 12, color: intelColors.primaryDark }}>
          {lookupLoad ? 'Looking up…' : lookupRes.error ? lookupRes.error : (
            <>📍 <strong>{lookupRes.pincode}</strong> — {lookupRes.matchedRows?.length || 0} couriers · Fwd {lookupRes.summary?.forwardCouriers || 0} · Rev {lookupRes.summary?.reverseCouriers || 0} · COD {lookupRes.summary?.codCouriers || 0} · SDD/NDD {lookupRes.summary?.sddNddCouriers || 0}</>
          )}
        </Box>
      )}

      {/* ── KPIs ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='Total Pincodes Covered (Union)' value={formatNumber(kpis.totalCovered?.value)} sub={`Across ${formatNumber(data?.universePinCount)} universe`}
          info='Unique pincodes served by at least one active code in this view — de-duplicated, so a pincode served by multiple codes counts once. Not the sum across codes.' />
        <KpiCard loading={loading} color='orange' label='SDD-Eligible Coverage' value={formatNumber(kpis.sddCoverage?.value)} sub={`${formatPct(kpis.sddCoverage?.pctOfUniverse)} of universe`}
          info='Unique pincodes covered by codes flagged SDD-eligible, shown as a % of the total pincode universe.' />
        <KpiCard loading={loading} color='red' label='Max Single-Courier Coverage' value={formatNumber(kpis.maxCourierCoverage?.value)} sub={`Top: ${(kpis.maxCourierCoverage?.attributionCodes || [])[0] || '—'}`}
          info='The widest reach of any single courier code in this view (the best individual code) — not the combined union. The "Top" code is named below.' />
        <KpiCard loading={loading} color='green' label='COD-Enabled Codes' value={formatNumber(kpis.codCoverage?.value)} sub={`${formatPct(kpis.codCoverage?.pctOfCodes)} of codes`}
          info='Number of account codes in this view that support COD, shown as a % of all codes in scope.' />
      </Box>

      {/* ── Tier strip ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', mb: 2.5 }}>
        {loading
          ? [0, 1].map((i) => (
              <Box key={i} sx={{ background: intelColors.surfaceBg, border: `1px solid ${intelColors.border}`, borderRadius: '6px', padding: '10px 14px' }}>
                <Skeleton variant='text' animation='wave' width='40%' sx={{ fontSize: 11 }} />
                <Skeleton variant='text' animation='wave' width='55%' sx={{ fontSize: 18 }} />
                <Skeleton variant='text' animation='wave' width='25%' sx={{ fontSize: 11 }} />
              </Box>
            ))
          : tiers.map((t) => {
              const isGood = (t.pct || 0) >= 95
              const bg = isGood ? intelColors.badgeGreenBg : t.pct >= 80 ? '#fff8e1' : intelColors.badgeRedBg
              const border = isGood ? '#aed581' : t.pct >= 80 ? '#ffd54f' : '#ef9a9a'
              const fg = isGood ? intelColors.greenDark : t.pct >= 80 ? '#bf7700' : intelColors.redDark
              return (
                <Box key={t.tier} sx={{ background: bg, border: `1px solid ${border}`, borderRadius: '6px', padding: '10px 14px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
                    <Typography sx={{ fontSize: 11, color: fg, fontWeight: 700, textTransform: 'uppercase' }}>{t.tier?.replace('_', ' ')}</Typography>
                    <MuiTooltip
                      arrow
                      placement='top'
                      title={`Of the ${t.totalCodes} account code(s) serving ${t.tier?.replace('_', ' ').toLowerCase()} pincodes in this view, ${t.servingCodes} actually cover them (${formatPct(t.pct)}).`}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13, color: fg, opacity: 0.7, cursor: 'help' }} />
                    </MuiTooltip>
                  </Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: intelColors.greenDark }}>{t.servingCodes} / {t.totalCodes} codes</Typography>
                  <Typography sx={{ fontSize: 11, color: fg }}>{formatPct(t.pct)}</Typography>
                </Box>
              )
            })}
      </Box>

      {/* ── Top Couriers chart ── */}
      {topCouriers.length > 0 && (
        <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', padding: '14px 18px', mb: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: intelColors.text }}>
            Pincode Coverage by Courier — Top {topCouriers.length}
          </Typography>
          <Typography sx={{ fontSize: 11, color: intelColors.muted, mb: 1.5 }}>
            Distinct delivery pincodes per parent courier (dropPincodesPrepaid ∪ dropPincodesCOD, de-duplicated)
          </Typography>
          <ResponsiveContainer width='100%' height={Math.max(170, topCouriers.length * 34)}>
            <BarChart data={topCouriers} layout='vertical' margin={{ left: 24, right: 48, top: 4, bottom: 4 }}>
              <XAxis type='number' tickFormatter={formatNumber} tick={{ fontSize: 11 }} />
              <YAxis type='category' dataKey='parent' width={150} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [formatNumber(v), 'Distinct pincodes']} cursor={{ fill: intelColors.rowHover }} />
              <Bar dataKey='distinctPincodes' fill={intelColors.primary} radius={[0, 4, 4, 0]} barSize={18}
                label={{ position: 'right', fontSize: 11, fill: intelColors.subtext, formatter: formatNumber }} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
         SHIPMENT MODE FILTER — global strip above all three data sections
         ════════════════════════════════════════════════════════════════════ */}
      <Box sx={{
        background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px',
        px: 2.5, py: 2, mb: 2,
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: intelColors.text, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
          Shipment Mode
        </Typography>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          {DIRECTIONS.map((d) => {
            const isActive = direction === d.val
            const colors = d.val === 'FORWARD'
              ? { bg: intelColors.badgeBlueBg, border: intelColors.primary, text: intelColors.primary }
              : d.val === 'REVERSE'
              ? { bg: intelColors.badgeOrangeBg, border: intelColors.orange, text: intelColors.orangeDark }
              : { bg: intelColors.badgeGreenBg, border: intelColors.green, text: intelColors.greenDark }
            return (
              <Box
                key={d.val}
                onClick={() => setDirection(d.val)}
                sx={{
                  px: 2, py: 0.8,
                  borderRadius: '8px',
                  fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  border: `1.5px solid ${isActive ? colors.border : intelColors.border}`,
                  background: isActive ? colors.bg : '#fff',
                  color: isActive ? colors.text : intelColors.subtext,
                  '&:hover': { borderColor: colors.border, background: colors.bg },
                }}
              >
                {d.label}
              </Box>
            )
          })}
        </Stack>
        {direction !== 'ALL' && (
          <Typography sx={{ fontSize: 11.5, color: direction === 'FORWARD' ? intelColors.primary : intelColors.orangeDark, fontWeight: 500, mt: 1 }}>
            {direction === 'FORWARD'
              ? 'Showing data for forward shipment mode only — all tables below are filtered to forward couriers and service types.'
              : 'Showing data for reverse shipment mode only — all tables below are filtered to reverse (RVP) couriers and service types.'}
          </Typography>
        )}
      </Box>

      {err && (
        <Box sx={{ background: intelColors.badgeRedBg, border: '1px solid #ef9a9a', color: intelColors.redDark, borderRadius: '6px', p: '10px 14px', mb: 1.5, fontSize: 12 }}>
          {err}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════════════
         THREE COLLAPSIBLE SECTIONS — prominent headers, click to expand
         ════════════════════════════════════════════════════════════════════ */}

      {/* ── 1. Service Types ── */}
      <SectionHeader
        sectionKey='stype' expanded={expanded.stype} onToggle={toggleSection('stype')}
        count={`${stypeRows.length} types`}
        subtitle={`${stypeActiveCount} active${directionLabel ? ` · ${directionLabel}` : ''}`}
      />
      <Collapse in={expanded.stype} timeout={200} unmountOnExit>
        <Box sx={{ border: `1px solid ${SECTION_THEME.stype.accent}`, borderTop: 'none', borderRadius: '0 0 8px 8px', mb: 1.5, overflow: 'hidden' }}>
          <IntelTable
            title='Delivery Coverage — Service Types'
            loading={loading}
            columns={stypeColumns}
            rows={stypeRows}
            rowKey={(r, i) => r.code || i}
            pageSize={50}
            action={<ExportCsvButton disabled={!stypeRows.length} onClick={() => exportCsv('serviceability_service_types.csv', STYPE_CSV, stypeRows)} />}
          />
        </Box>
      </Collapse>

      {/* ── 2. Couriers ── */}
      <SectionHeader
        sectionKey='courier' expanded={expanded.courier} onToggle={toggleSection('courier')}
        count={courierFresh ? `${courierRows.length} parents` : '— · expand to load'}
        subtitle={courierFresh ? `${courierFwdCount} fwd · ${courierRevCount} rev${directionLabel ? ` · ${directionLabel}` : ''}` : ''}
      />
      <Collapse in={expanded.courier} timeout={200} unmountOnExit>
        <Box sx={{ border: `1px solid ${SECTION_THEME.courier.accent}`, borderTop: 'none', borderRadius: '0 0 8px 8px', mb: 1.5, overflow: 'hidden' }}>
          <IntelTable
            title='Delivery Coverage — Couriers'
            loading={courierLoading}
            columns={courierColumns}
            rows={courierRows}
            rowKey={(r, i) => r.parent || i}
            pageSize={50}
            action={<ExportCsvButton disabled={!courierRows.length} onClick={() => exportCsv('serviceability_couriers.csv', COURIER_CSV, courierRows)} />}
          />
        </Box>
      </Collapse>

      {/* ── 3. Courier Codes ── */}
      <SectionHeader
        sectionKey='code' expanded={expanded.code} onToggle={toggleSection('code')}
        count={codeFresh ? `${codeRows.length} codes` : '— · expand to load'}
        subtitle={codeFresh ? `${codeFwdCount} fwd · ${codeRevCount} rev${directionLabel ? ` · ${directionLabel}` : ''}` : ''}
      />
      <Collapse in={expanded.code} timeout={200} unmountOnExit>
        <Box sx={{ border: `1px solid ${SECTION_THEME.code.accent}`, borderTop: 'none', borderRadius: '0 0 8px 8px', mb: 1.5, overflow: 'hidden' }}>
          <IntelTable
            title='Delivery Coverage — Courier Codes'
            loading={codeLoading}
            columns={codeColumns}
            rows={codeRows}
            rowKey={(r, i) => r.cpAccountCode || i}
            pageSize={50}
            action={<ExportCsvButton disabled={!codeRows.length} onClick={() => exportCsv('serviceability_courier_codes.csv', CODE_CSV, codeRows)} />}
          />
        </Box>
      </Collapse>
    </IntelLayout>
  )
}
